import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import {
  applySuggestionApi,
  draftCampaignAiApi,
  getAiStatusApi,
  listCampaignStoresApi,
  nextCampaignAiApi,
  suggestionToPayload,
  type AiStatus,
  type AiSuggestion,
  type CampaignStore,
  type DraftInterview,
} from "../campanhasApi";
import AiAssistantDrawer from "../components/AiAssistantDrawer";
import SuggestionRefineChat from "../components/SuggestionRefineChat";
import SuggestionReview from "../components/SuggestionReview";
import { CAMPANHAS_PAGE, CAMPANHA_PAGE } from "../routes";
import "./Campanhas.css";

const inMonth = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toLocaleDateString("en-CA");
};

/**
 * Criar campanha com o assistente: a pessoa responde perguntas simples, a IA
 * monta a campanha de teste e explica as escolhas; criar o rascunho é um clique.
 * Com `:id` na rota, é "a próxima com base nesta": a IA lê os resultados.
 */
const CampanhaAssistentePage: React.FC = () => {
  const { id: sourceId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack(sourceId ? CAMPANHA_PAGE(sourceId) : CAMPANHAS_PAGE());
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [stores, setStores] = useState<CampaignStore[]>([]);
  const [form, setForm] = useState<DraftInterview>({ role: "", storeIds: [], where: "", peopleNeeded: 5, deadline: inMonth(), totalBudget: 300, offer: "", requirements: "", notes: "" });
  const [suggestion, setSuggestion] = useState<AiSuggestion | null>(null);
  const [keepReuse, setKeepReuse] = useState<string[]>([]);
  const [thinking, setThinking] = useState(false);
  const [applying, setApplying] = useState(false);
  // Rascunho salvo assim que a Eco-IA responde; cada ajuste atualiza o mesmo.
  const [draftId, setDraftId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // "Pedir outra sugestão" na próxima: uma nova rodada.
  const [round, setRound] = useState(0);

  useEffect(() => {
    getAiStatusApi().then(setStatus).catch(() => setStatus({ enabled: false, spentUsd: 0, limitUsd: 0, reached: false }));
    if (!sourceId) listCampaignStoresApi().then(setStores).catch(() => setStores([]));
  }, [sourceId]);

  /** Grava a sugestão como rascunho (ou atualiza o já gravado). Falhar não tira a sugestão da tela. */
  const save = async (s: AiSuggestion, existingId: string | null) => {
    try {
      setApplying(true);
      const payload = suggestionToPayload(s, existingId ? [] : (s.reuseAds ?? []).map((a) => a.id));
      const saved = await applySuggestionApi(existingId ? { ...payload, campaignId: existingId } : payload);
      setDraftId(saved.id);
      setSaveError(null);
      toast.success(existingId ? "Rascunho atualizado." : "Rascunho salvo. Pode atualizar a tela sem perder nada: ele está em Campanhas.");
    } catch (err: any) {
      setSaveError(err?.message ?? "Não foi possível salvar o rascunho.");
    } finally {
      setApplying(false);
    }
  };

  const showSuggestion = (s: AiSuggestion) => {
    setSuggestion(s);
    setKeepReuse((s.reuseAds ?? []).map((a) => a.id));
    setDraftId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    save(s, null);
  };

  const onRefined = (s: AiSuggestion) => {
    setSuggestion(s);
    save(s, draftId);
  };

  // "A próxima": pede direto, sem conversa.
  useEffect(() => {
    if (!sourceId || !status?.enabled || status.reached || suggestion) return;
    let cancelled = false;
    setThinking(true);
    nextCampaignAiApi(sourceId)
      .then((s) => !cancelled && showSuggestion(s))
      .catch((e) => !cancelled && toast.error(e?.message))
      .finally(() => !cancelled && setThinking(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, status, round]);

  const ask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.role.trim()) return toast.error("Diga qual é a vaga.");
    if (!form.storeIds?.length && !form.where?.trim()) return toast.error("Escolha uma loja ou escreva os bairros ou cidades.");
    try {
      setThinking(true);
      showSuggestion(
        await draftCampaignAiApi({
          ...form,
          role: form.role.trim(),
          where: form.where?.trim() || undefined,
          offer: form.offer?.trim() || undefined,
          requirements: form.requirements?.trim() || undefined,
          notes: form.notes?.trim() || undefined,
        }),
      );
    } catch (err: any) {
      toast.error(err?.message);
    } finally {
      setThinking(false);
    }
  };

  const openDraft = () => {
    if (!draftId) return;
    navigate(`${CAMPANHA_PAGE(draftId)}?aba=anuncios`, { replace: true });
  };

  const set = <K extends keyof DraftInterview>(key: K, value: DraftInterview[K]) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleStore = (id: string) => set("storeIds", form.storeIds?.includes(id) ? form.storeIds.filter((s) => s !== id) : [...(form.storeIds ?? []), id]);
  const unavailable = status && (!status.enabled || status.reached);

  return (
    <div className="cmpPage">
      <main className="cmpPage__main cmpPage__main--narrow">
        <EcoPageHeader
          crumbs={[{ label: "Campanhas", onClick: () => navigate(CAMPANHAS_PAGE()) }, { label: sourceId ? "Próxima campanha" : "Criar com a Eco-IA" }]}
          title={sourceId ? "Próxima campanha com a Eco-IA" : "Criar campanha com a Eco-IA"}
          subtitle={
            sourceId
              ? "A Eco-IA lê os resultados da campanha anterior e sugere a próxima: mantém o que funcionou e muda o que não funcionou."
              : "Responda algumas perguntas simples. A Eco-IA monta uma campanha de teste e explica cada escolha. Nada é publicado: você revisa e cria o rascunho."
          }
          onBack={goBack}
          backLabel="Voltar"
        />

        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 20 }}>
          {unavailable ? (
            <p className="cmpNotice">
              <EcoIcon name="alertTriangle" size={16} />
              <span>
                {status?.reached
                  ? `O limite de gasto com IA do mês (US$ ${status.limitUsd.toFixed(2)}) foi atingido.`
                  : "A Eco-IA ainda não está ligada no servidor (falta a chave da IA). Dá para criar a campanha pelo formulário."}
              </span>
            </p>
          ) : null}

          {thinking ? (
            <section className="ecoCard cmpPanel aiThinking" role="status">
              <span className="aiThinking__dot" aria-hidden="true" />
              <div>
                <strong>A Eco-IA está montando a campanha...</strong>
                <p className="cmpHint">Ele lê o que você respondeu{sourceId ? " e os resultados da campanha anterior" : ""}. Leva até um minuto.</p>
              </div>
            </section>
          ) : null}

          {!sourceId && !suggestion && !thinking ? (
            <form className="ecoCard cmpPanel" onSubmit={ask}>
              <h2 className="cmpPanel__title">Conte sobre a vaga</h2>
              <label className="ecoField">
                <span className="ecoLabel">Para que vaga você precisa de gente?</span>
                <input className="ecoInput" maxLength={300} value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="Ex.: entregador de moto para as lojas da Zona Sul" />
              </label>

              <div className="ecoField">
                <span className="ecoLabel">Perto de quais lojas?</span>
                {stores.length ? (
                  <div className="aiStores">
                    {stores.map((s) => (
                      <label key={s.id} className="cmpCheck">
                        <input type="checkbox" checked={Boolean(form.storeIds?.includes(s.id))} onChange={() => toggleStore(s.id)} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="cmpHint">Nenhuma loja com posição cadastrada. Use o campo abaixo.</p>
                )}
              </div>
              <label className="ecoField">
                <span className="ecoLabel">
                  Ou em quais bairros ou cidades? <span className="ecoLabel__optional">(opcional)</span>
                </span>
                <input className="ecoInput" maxLength={300} value={form.where} onChange={(e) => set("where", e.target.value)} placeholder="Ex.: Copacabana, Botafogo e Niterói" />
              </label>

              <div className="cmpGrid cmpGrid--3">
                <label className="ecoField">
                  <span className="ecoLabel">Quantas pessoas precisa?</span>
                  <input className="ecoInput" type="number" min={1} max={1000} value={form.peopleNeeded} onChange={(e) => set("peopleNeeded", Number(e.target.value))} />
                </label>
                <label className="ecoField">
                  <span className="ecoLabel">Até quando?</span>
                  <input className="ecoInput" type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} />
                </label>
                <label className="ecoField">
                  <span className="ecoLabel">Quanto pode investir no total (R$)?</span>
                  <input className="ecoInput" type="number" min={1} step={1} value={form.totalBudget} onChange={(e) => set("totalBudget", Number(e.target.value))} />
                </label>
              </div>

              <label className="ecoField">
                <span className="ecoLabel">
                  O que a vaga oferece? <span className="ecoLabel__optional">(pagamento, horários, benefícios)</span>
                </span>
                <textarea className="ecoInput cmpTextarea" rows={2} maxLength={1000} value={form.offer} onChange={(e) => set("offer", e.target.value)} placeholder="Ex.: diária de R$ 120 paga toda sexta, fins de semana, lanche" />
              </label>
              <label className="ecoField">
                <span className="ecoLabel">
                  Requisitos <span className="ecoLabel__optional">(opcional)</span>
                </span>
                <textarea className="ecoInput cmpTextarea" rows={2} maxLength={1000} value={form.requirements} onChange={(e) => set("requirements", e.target.value)} placeholder="Ex.: moto própria com documentação, morar perto" />
              </label>
              <label className="ecoField">
                <span className="ecoLabel">
                  Mais alguma coisa? <span className="ecoLabel__optional">(opcional)</span>
                </span>
                <textarea className="ecoInput cmpTextarea" rows={2} maxLength={1000} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              </label>

              <p className="cmpHint">A Eco-IA não vê nenhum dado de candidato, só números. Ela só sugere: nada é criado sem você clicar.</p>
              <div className="cmpFooter">
                <button type="button" className="ecoBtn" onClick={goBack}>
                  Cancelar
                </button>
                <button type="submit" className="ecoBtn ecoBtn--primary" disabled={Boolean(unavailable)}>
                  <EcoIcon name="helpCircle" size={18} />
                  Sugerir a campanha
                </button>
              </div>
            </form>
          ) : null}

          {suggestion && !thinking ? (
            <>
              {saveError ? (
                <p className="cmpNotice cmpNotice--bad">
                  <EcoIcon name="alertTriangle" size={16} />
                  <span>
                    O rascunho não foi salvo: {saveError} Peça o ajuste à Eco-IA (por exemplo, a região) ou tente de novo.
                  </span>
                </p>
              ) : draftId ? (
                <p className="cmpNotice cmpNotice--info">
                  <EcoIcon name="check" size={16} />
                  <span>Rascunho salvo em Campanhas. Cada ajuste pedido à Eco-IA atualiza o mesmo rascunho; nada vai para a Meta antes de você publicar.</span>
                </p>
              ) : null}
              <SuggestionRefineChat suggestion={suggestion} onChange={onRefined} disabled={applying} />
              <SuggestionReview
                suggestion={suggestion}
                keepReuse={keepReuse}
                onToggleReuse={
                  draftId ? undefined : (adId) => setKeepReuse((prev) => (prev.includes(adId) ? prev.filter((x) => x !== adId) : [...prev, adId]))
                }
              />
              <div className="cmpFooter">
                <button
                  type="button"
                  className="ecoBtn"
                  onClick={() => {
                    setSuggestion(null);
                    setDraftId(null);
                    setSaveError(null);
                    if (sourceId) setRound((r) => r + 1);
                  }}
                  disabled={applying}
                >
                  {sourceId ? "Pedir outra sugestão" : "Mudar as respostas"}
                </button>
                {draftId ? (
                  <button type="button" className="ecoBtn ecoBtn--primary" onClick={openDraft} disabled={applying}>
                    {applying ? "Salvando..." : "Abrir o rascunho"}
                  </button>
                ) : (
                  <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => suggestion && save(suggestion, null)} disabled={applying}>
                    {applying ? "Salvando..." : "Tentar salvar o rascunho de novo"}
                  </button>
                )}
              </div>
              <p className="cmpHint" style={{ textAlign: "right" }}>
                No rascunho, você inclui os anúncios com imagem e pode mudar tudo no formulário antes de publicar.
              </p>
            </>
          ) : null}
        </div>
      </main>
      <AiAssistantDrawer />
    </div>
  );
};

export default CampanhaAssistentePage;
